import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Tag, ArrowLeft } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { trackEvent } from '../lib/analytics';
import SEO from '../components/SEO';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
  language: string;
  author: string;
  featured: boolean;
  translationKey: string;
  slug: string;
}

export const Blog = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const currentLanguage = i18n.language;
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentSlug, setCurrentSlug] = useState<string | undefined>(slug);
  const { isAllowed } = useCookieConsent();

  useEffect(() => {
    // AI-WOS post
    const aiWosPost = {
      id: 'ai-wos',
      title: currentLanguage === 'it' 
        ? "AI-WOS: Il Sistema Operativo che Cambierà per Sempre il Modo in cui le Aziende Lavorano"
        : "AI-WOS: The Operating System That Will Forever Change How Companies Work",
      excerpt: currentLanguage === 'it'
        ? "Le aziende che non useranno un cervello digitale... saranno governate da chi lo ha."
        : "Companies that don't use a digital brain... will be governed by those who do.",
      date: '2025-03-23',
      readTime: '6 min',
      tags: ['AI', 'Business', currentLanguage === 'it' ? 'Innovazione' : 'Innovation'],
      image: 'https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/main/AI%20WOS%20Edited.jpg',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: true,
      translationKey: 'ai-wos',
      slug: 'ai-wos'
    };
    
    // Video Emails post (adds the new blog post)
    const videoEmailsPost = {
      id: 'personalized-video-emails',
      title: currentLanguage === 'it' 
        ? "Mail con Video Personalizzati: Il Futuro della Comunicazione Aziendale e Personale" 
        : "Personalized Video Emails: The Future of Business and Personal Communication",
      excerpt: currentLanguage === 'it' 
        ? "Nel mondo iperconnesso di oggi, le comunicazioni aziendali stanno vivendo una rivoluzione. L'email tradizionale non è più sufficiente." 
        : "In today's hyperconnected world, business communications are experiencing a revolution. Traditional email is no longer enough.",
      date: '2025-02-22',
      readTime: currentLanguage === 'it' ? '5 min di lettura' : '5 min read',
      tags: ['marketing', currentLanguage === 'it' ? 'automazione' : 'automation', 'AI'],
      image: 'https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/main/video%20mail%20avatar.png',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: false,
      translationKey: 'video-emails',
      slug: 'personalized-video-emails'
    };
    // PMI Digital post
    const pmiDigitalPost = {
      id: 'why-pmi-fall-behind',
      title: currentLanguage === 'it'
        ? "Perché tante PMI italiane restano indietro? I 3 errori che vedo ogni giorno"
        : "Why Do So Many Italian SMEs Fall Behind? The 3 Mistakes I See Every Day",
      excerpt: currentLanguage === 'it'
        ? "Non è colpa della crisi. Né dell'AI. È che molte aziende continuano a commettere sempre gli stessi errori."
        : "It's not the crisis's fault. Nor is it AI's. It's that many companies keep making the same mistakes.",
      date: '2025-03-27',
      readTime: currentLanguage === 'it' ? '6 min di lettura' : '6 min read',
      tags: ['Business', currentLanguage === 'it' ? 'Trasformazione Digitale' : 'Digital Transformation', currentLanguage === 'it' ? 'PMI' : 'SMEs'],
      image: 'https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/main/process%20mapping.png',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: true,
      translationKey: 'why-pmi-fall-behind',
      slug: 'why-pmi-fall-behind'
    };

    // Set the posts and filter by language
    const allPosts = [aiWosPost, videoEmailsPost, pmiDigitalPost].filter(post => post.language === currentLanguage);
    setPosts(allPosts);
  }, [currentLanguage]);

  // Find current post if slug is provided
  const currentPost = slug ? posts.find(post => post.slug === slug) : null;

  // If a slug is provided but no matching post was found
  if (slug && !currentPost && posts.length > 0) {
    navigate('/blog');
    return null;
  }

  // Example of using isAllowed to conditionally track an event
  const handleReadBlogPost = (postSlug: string, postTitle: string) => {
    if (isAllowed('analytics')) {
      // Only track if analytics cookies are allowed
      trackEvent('Blog', 'Read Post', `${postSlug}: ${postTitle}`);
    }
    // Continue with navigation logic
    setCurrentSlug(postSlug);
  };

  // Render single blog post
  if (currentPost) {
    return (
      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto bg-white dark:bg-gray-900 min-h-screen">
        <SEO
          title={`${currentPost.title} – Victor Lazari`}
          description={currentPost.excerpt}
          canonical={`https://victorlazari.com/blog/${currentPost.slug}`}
          image={currentPost.image}
          type="article"
          publishedTime={new Date(currentPost.date).toISOString()}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: currentPost.title,
            datePublished: currentPost.date,
            author: {
              '@type': 'Person',
              name: 'Victor Lazari'
            },
            image: [currentPost.image],
            mainEntityOfPage: `https://victorlazari.com/blog/${currentPost.slug}`
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-8"
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center text-sky-700 dark:text-sky-400 hover:text-sky-900 dark:hover:text-sky-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('blog.backToList')}
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-slate-200 dark:border-gray-700 shadow-sm mb-8"
        >
          <div className="relative h-60 md:h-80 overflow-hidden">
            <img
              src={currentPost.image}
              alt={currentPost.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-40" />
          </div>

          <div className="p-8">
            <div className="flex flex-wrap gap-2 mb-6">
              {currentPost.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 text-sm rounded-full bg-slate-100 dark:bg-gray-700/50 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 flex items-center gap-1"
                >
                  <Tag className="w-4 h-4 mr-1" />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl font-semibold mb-6 text-slate-900 dark:text-gray-100">
              {currentPost.title}
            </h1>

            <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-gray-400 mb-8">
              <span className="flex items-center gap-1">
                <Calendar className="w-5 h-5" />
                {new Date(currentPost.date).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-5 h-5" />
                {currentPost.readTime}
              </span>
            </div>

            {slug === 'ai-wos' && (
              <div className="prose prose-lg dark:prose-invert max-w-none">
                {currentLanguage === 'it' ? (
                  // Italian version
                  <>
                    <blockquote>
                      <p>Le aziende che non useranno un cervello digitale... saranno governate da chi lo ha.</p>
                    </blockquote>

                    <h2>Il Futuro del Lavoro Aziendale</h2>

                    <p>Immagina di entrare nel tuo ufficio e chiedere semplicemente: "Come stiamo andando questo mese?"</p>

                    <p>Una voce ti risponde con chiarezza: analizza le vendite, legge il CRM, valuta i costi, considera le campagne marketing, e in pochi secondi ti mostra una panoramica strategica completa. Ma non solo: ti propone le 3 azioni più efficaci da fare—e può anche eseguirle per te.</p>

                    <p>Questa non è fantascienza. È AI-WOS.</p>

                    <h2>Cos'è AI-WOS?</h2>

                    <p>AI-WOS (AI Workflow Operating System) è il cuore pulsante dell'impresa moderna. Non è un'app. Non è un chatbot. È un sistema nervoso centrale per l'azienda: unisce persone, processi e dati, permettendo all'intelligenza artificiale di orchestrare l'operatività in tempo reale.</p>

                    <p>Utilizza modelli linguistici (come GPT) solo come interfaccia. Ma il vero potere è sotto il cofano:</p>

                    <ul>
                      <li>Collega CRM, ERP, email, progetti, vendite, risorse umane.</li>
                      <li>Legge tutto, capisce tutto, agisce.</li>
                      <li>Coordina agenti AI specializzati in vendite, finanza, marketing, HR, operations.</li>
                    </ul>

                    <h2>Perché È La Svolta per le Aziende Italiane?</h2>

                    <p>In Italia, molte imprese vivono ancora in una giungla digitale: software sparsi, report Excel, workflow manuali, silos informativi. L'imprenditore è spesso sovraccarico, il middle management opera a vista, e le decisioni arrivano troppo tardi.</p>

                    <p>AI-WOS cambia tutto questo.</p>

                    <h3>1. Decisioni Istantanee</h3>

                    <p>Da reattivi a proattivi.</p>

                    <p>🧠 Simulazione: Prevenzione Abbandono Clienti</p>

                    <p>Situazione: il tasso di chiusura dei preventivi a Roma cala del 17% in due settimane.</p>

                    <p>Azione: AI-WOS intercetta l'anomalia, analizza lo storico CRM, nota un aumento delle risposte "ci devo pensare".</p>

                    <p>Risultato: propone 3 azioni immediate:</p>

                    <ul>
                      <li>Offerta promozionale mirata su clienti indecisi</li>
                      <li>Nuovo script per follow-up</li>
                      <li>Intervento diretto del direttore vendite su top clienti in stallo</li>
                    </ul>

                    <h3>2. Esecuzione Intelligente</h3>

                    <p>Da manuale a automatizzata. L'AI non si limita a consigliare. Può agire:</p>

                    <p>⚙️ Simulazione: Automatizzazione Fatturazione</p>

                    <p>Domanda: "AI-WOS, quali fatture non sono ancora state emesse questo mese?"</p>

                    <p>Risposta: Elenco di 12 clienti con lavori completati ma senza fattura</p>

                    <p>Azione proposta: invio automatico delle fatture entro 12 ore, con reminder ai project manager</p>

                    <h3>3. Collaborazione Umana Potenziata</h3>

                    <p>Da strumenti isolati a ecosistema coeso. I tuoi dipendenti non devono più imparare 8 strumenti diversi. Basta chiedere all'AI, e lei si occupa di tutto, orchestrando:</p>

                    <ul>
                      <li>Task management</li>
                      <li>Email</li>
                      <li>Inserimenti nel CRM</li>
                      <li>Notifiche su Slack o Teams</li>
                    </ul>

                    <h2>ROI Reale, Non Storytelling</h2>

                    <p>Ogni azione generata da AI-WOS è tracciabile, reversibile, e soprattutto misurabile.</p>

                    <blockquote>
                      <p>"Nel primo mese di utilizzo abbiamo recuperato 22.000€ da clienti che stavamo per perdere."</p>
                      <p>"Il tempo medio di analisi del nostro team vendite si è ridotto del 47%."</p>
                      <p>"I nostri manager hanno finalmente una visione unificata senza dover chiedere 5 report."</p>
                    </blockquote>

                    <h2>Chi Dovrebbe Ascoltare Questo Messaggio?</h2>

                    <p>Se sei un:</p>

                    <ul>
                      <li>CEO che vuole visibilità in tempo reale su performance e KPI</li>
                      <li>Direttore vendite che vuole capire perché i deal non chiudono</li>
                      <li>CFO che vuole report predittivi senza aspettare il fine mese</li>
                      <li>Founder che vuole scalare senza perdere il controllo operativo</li>
                    </ul>

                    <p>AI-WOS è la tua infrastruttura mentale.</p>

                    <h2>Cosa Lo Rende Così Diverso?</h2>

                    <p>AI-WOS non è una piattaforma di AI "giocattolo". È costruito secondo i principi dei sistemi militari (come Palantir AIP) ma per uso aziendale:</p>

                    <ul>
                      <li>Accesso basato su ruoli (reps vedono solo i propri dati, manager vedono l'intero reparto)</li>
                      <li>Log tracciabili per ogni azione proposta o eseguita</li>
                      <li>Sicurezza e controllo: nessuna azione viene eseguita senza autorizzazione o policy</li>
                      <li>Simulazione e confronto: ogni decisione viene calcolata con almeno 2 scenari alternativi</li>
                    </ul>

                    <h2>Il Futuro delle Aziende È AI-Nativo</h2>

                    <p>Le aziende vincenti dei prossimi anni non saranno quelle che "usano l'AI", ma quelle che pensano, agiscono e si muovono come un'intelligenza artificiale collettiva.</p>

                    <p>Il cervello aziendale non è più solo umano. È una rete ibrida, dove ogni dato alimenta una macchina predittiva e ogni azione è calibrata con precisione chirurgica.</p>

                    <p>⚠️ Spoiler alert: Sto costruendo proprio questo. In Italia. Con precisione, velocità e una visione molto chiara.</p>

                    <p>Se sei un imprenditore, investitore o innovatore, scrivimi. I primi che capiscono... guidano il cambiamento.</p>

                    <p className="text-sm text-gray-400 mt-8">#AIWOS #IntelligenzaArtificiale #CEOItalia #DigitalTransformation #FutureOfWork #AutomazioneIntelligente #NextGenOps</p>
                  </>
                ) : (
                  // English version
                  <>
                    <blockquote>
                      <p>Companies that don't use a digital brain... will be governed by those who do.</p>
                    </blockquote>

                    <h2>The Future of Business Operations</h2>

                    <p>Imagine walking into your office and simply asking: "How are we doing this month?"</p>

                    <p>A voice responds with clarity: analyzing sales, reading the CRM, evaluating costs, considering marketing campaigns, and in a few seconds, showing you a complete strategic overview. But not only that: it suggests the 3 most effective actions to take—and can even execute them for you.</p>

                    <p>This isn't science fiction. It's AI-WOS.</p>

                    <h2>What is AI-WOS?</h2>

                    <p>AI-WOS (AI Workflow Operating System) is the beating heart of the modern enterprise. It's not an app. It's not a chatbot. It's a central nervous system for the company: it unites people, processes, and data, allowing artificial intelligence to orchestrate operations in real-time.</p>

                    <p>It uses language models (like GPT) only as an interface. But the real power is under the hood:</p>

                    <ul>
                      <li>Connects CRM, ERP, email, projects, sales, human resources.</li>
                      <li>Reads everything, understands everything, acts.</li>
                      <li>Coordinates specialized AI agents in sales, finance, marketing, HR, operations.</li>
                    </ul>

                    <h2>Why is it a breakthrough for companies?</h2>

                    <p>Many businesses today still operate in a digital jungle: scattered software, Excel reports, manual workflows, information silos. Executives are often overwhelmed, middle management operates with limited visibility, and decisions come too late.</p>

                    <p>AI-WOS changes all of this.</p>

                    <h3>1. Instant Decisions</h3>

                    <p>From reactive to proactive.</p>

                    <p>🧠 Simulation: Customer Churn Prevention</p>

                    <p>Situation: Quote closure rate in New York drops 17% in two weeks.</p>

                    <p>Action: AI-WOS intercepts the anomaly, analyzes CRM history, notices an increase in "I'll think about it" responses.</p>

                    <p>Result: proposes 3 immediate actions:</p>

                    <ul>
                      <li>Targeted promotional offer for undecided customers</li>
                      <li>New follow-up script</li>
                      <li>Direct sales director intervention on stalled top clients</li>
                    </ul>

                    <h3>2. Intelligent Execution</h3>

                    <p>From manual to automated. AI doesn't just advise. It can act:</p>

                    <p>⚙️ Simulation: Invoice Automation</p>

                    <p>Question: "AI-WOS, which invoices haven't been issued this month?"</p>

                    <p>Response: List of 12 clients with completed work but no invoice</p>

                    <p>Proposed action: automatic invoice sending within 12 hours, with reminders to project managers</p>

                    <h3>3. Enhanced Human Collaboration</h3>

                    <p>From isolated tools to a cohesive ecosystem. Your employees no longer need to learn 8 different tools. Just ask the AI, and it takes care of everything, orchestrating:</p>

                    <ul>
                      <li>Task management</li>
                      <li>Email</li>
                      <li>CRM entries</li>
                      <li>Notifications on Slack or Teams</li>
                    </ul>

                    <h2>Real ROI, Not Storytelling</h2>

                    <p>Every action generated by AI-WOS is traceable, reversible, and most importantly, measurable.</p>

                    <blockquote>
                      <p>"In the first month of use, we recovered $24,000 from customers we were about to lose."</p>
                      <p>"The average analysis time of our sales team has been reduced by 47%."</p>
                      <p>"Our managers finally have a unified view without having to request 5 reports."</p>
                    </blockquote>

                    <h2>Who Should Listen to This Message?</h2>

                    <p>If you are a:</p>

                    <ul>
                      <li>CEO who wants real-time visibility on performance and KPIs</li>
                      <li>Sales director who wants to understand why deals aren't closing</li>
                      <li>CFO who wants predictive reports without waiting for month-end</li>
                      <li>Founder who wants to scale without losing operational control</li>
                    </ul>

                    <p>AI-WOS is your mental infrastructure.</p>

                    <h2>What Makes It So Different?</h2>

                    <p>AI-WOS is not a "toy" AI platform. It's built according to military-grade system principles (like Palantir AIP) but for business use:</p>

                    <ul>
                      <li>Role-based access (reps see only their data, managers see the entire department)</li>
                      <li>Traceable logs for every proposed or executed action</li>
                      <li>Security and control: no action is executed without authorization or policy</li>
                      <li>Simulation and comparison: every decision is calculated with at least 2 alternative scenarios</li>
                    </ul>

                    <h2>The Future of Business is AI-Native</h2>

                    <p>The winning companies of the coming years won't be those that "use AI," but those that think, act, and move like a collective artificial intelligence.</p>

                    <p>The business brain is no longer just human. It's a hybrid network, where every piece of data feeds a predictive machine and every action is calibrated with surgical precision.</p>

                    <p>⚠️ Spoiler alert: I'm building exactly this, right now. With precision, speed, and a very clear vision.</p>

                    <p>If you're an entrepreneur, investor, or innovator, reach out. Those who understand first... lead the change.</p>

                    <p className="text-sm text-gray-400 mt-8">#AIWOS #ArtificialIntelligence #DigitalTransformation #FutureOfWork #IntelligentAutomation #NextGenOps</p>
                  </>
                )}
              </div>
            )}

            {slug === 'personalized-video-emails' && (
              <div className="prose prose-lg dark:prose-invert max-w-none">
                {currentLanguage === 'it' ? (
                  // Italian version
                  <>

                    <p>Nel mondo iperconnesso di oggi, le comunicazioni aziendali stanno vivendo una rivoluzione. L'email tradizionale, per quanto ancora efficace, non è più sufficiente per distinguersi davvero. Serve emozione, personalizzazione, impatto visivo.</p>

                    <p>Immagina ora un sistema in grado di:</p>

                    <p>✅ Trasformare il testo in voce umana realistica<br />
                    ✅ Utilizzare un avatar animato che parla come te<br />
                    ✅ Inviare email video personalizzate in automatico</p>

                    <p>Benvenuti nell'era delle Video Mail Personalizzate con Avatar AI.</p>

                    <h2>👔 A Chi Serve Davvero?</h2>

                    <p>Questa tecnologia è pensata per chi guida aziende, team e relazioni:<br />
                    📌 CEO, Imprenditori, Direttori Marketing, HR Manager, Venditori di alto livello.<br />
                    Chi comunica ogni giorno con clienti, investitori, fornitori o collaboratori.</p>

                    <h2>🔍 Esempi Concreti per il Tuo Business</h2>

                    <h3>1. Email Commerciali che Vendono di Più</h3>
                    <p>👉 Invece di una semplice email di presentazione, invii un video dove il tuo avatar ti rappresenta e parla direttamente al potenziale cliente.<br />
                    Effetto? +40% di apertura, +70% di conversione (dati di case study reali).</p>

                    <p>📌 Esempio concreto:<br />
                    Un'azienda che produce macchinari industriali ha creato video personalizzati per ogni cliente B2B, spiegando il prodotto in modo visivo. Il tasso di risposta è raddoppiato.</p>

                    <h3>2. Onboarding Innovativo per Nuovi Dipendenti</h3>
                    <p>👉 Invia un messaggio di benvenuto automatizzato e personalizzato, dove il CEO in avatar dà il benvenuto al nuovo assunto con il suo nome, in più lingue se necessario.</p>

                    <p>📌 Risultato? I dipendenti si sentono accolti, motivati e più coinvolti fin dal primo giorno.</p>

                    <h3>3. Auguri Aziendali o Annunci Importanti</h3>
                    <p>🎂 Vuoi fare gli auguri di compleanno a ogni collaboratore o cliente? Fallo con un avatar che dice il suo nome e un messaggio su misura.<br />
                    📣 Devi annunciare una nuova sede o un cambiamento importante? Fallo "in persona" senza dover registrare ogni volta un video.</p>

                    <h3>4. Campagne Multilingua Senza Costi Aggiuntivi</h3>
                    <p>🌍 Devi comunicare con clienti in Germania, Spagna, USA? Nessun problema.<br />
                    Con pochi click, il tuo avatar parlerà in tedesco, spagnolo o inglese con voce naturale e professionale.</p>

                    <h2>💡 Perché Funziona?</h2>
                    <p>✔ Umano ma scalabile – Hai l'impatto di una comunicazione personale, ma senza registrare mille video a mano.<br />
                    ✔ Automatizzabile – Può essere integrato con CRM o sistemi di marketing automation.<br />
                    ✔ Semplice – Ti basta scrivere un messaggio, il sistema fa tutto il resto.</p>

                    <h2>🚀 Cosa Può Fare per la Tua Azienda?</h2>
                    <p>Questa tecnologia è già disponibile e può essere personalizzata sulle tue esigenze.<br />
                    Posso aiutarti a:</p>

                    <ul>
                      <li>Implementare una soluzione in cloud per email video personalizzate</li>
                      <li>Creare flussi automatici di marketing personalizzato</li>
                      <li>Integrare l'avatar con il tuo CRM o sito web</li>
                      <li>Realizzare video con brand, logo e identità visiva della tua azienda</li>
                    </ul>

                    <h2>🎯 Vuoi Provare una Demo?</h2>
                    <p>Scrivimi nei commenti o in privato.<br />
                    📩 Ti invio un esempio fatto su misura per la tua azienda (gratuito).<br />
                    💬 Parliamone: basta un'idea per trasformare la comunicazione della tua impresa.</p>

                    <p>Il futuro della comunicazione è visivo, intelligente e personale.<br />
                    Chi lo adotta per primo, vince la gara dell'attenzione.</p>
                  </>
                ) : (
                  // English version
                  <>

                    <p>In today's hyperconnected world, business communications are experiencing a revolution. Traditional email, while still effective, is no longer enough to truly stand out. You need emotion, personalization, and visual impact.</p>

                    <p>Imagine a system capable of:</p>

                    <p>✅ Transforming text into realistic human voice<br />
                    ✅ Using an animated avatar that talks like you<br />
                    ✅ Sending personalized video emails automatically</p>

                    <p>Welcome to the era of Personalized Video Emails with AI Avatars.</p>

                    <h2>👔 Who Really Needs This?</h2>

                    <p>This technology is designed for those who lead companies, teams, and relationships:<br />
                    📌 CEOs, Entrepreneurs, Marketing Directors, HR Managers, High-level Sales Professionals.<br />
                    Anyone who communicates daily with clients, investors, suppliers, or collaborators.</p>

                    <h2>🔍 Concrete Examples for Your Business</h2>

                    <h3>1. Commercial Emails That Sell More</h3>
                    <p>👉 Instead of a simple introductory email, you send a video where your avatar represents you and speaks directly to the potential client.<br />
                    Effect? +40% open rate, +70% conversion rate (data from real case studies).</p>

                    <p>📌 Concrete example:<br />
                    A company that produces industrial machinery created personalized videos for each B2B client, explaining the product visually. The response rate doubled.</p>

                    <h3>2. Innovative Onboarding for New Employees</h3>
                    <p>👉 Send an automated and personalized welcome message, where the CEO's avatar welcomes the new hire by name, in multiple languages if necessary.</p>

                    <p>📌 Result? Employees feel welcomed, motivated, and more engaged from day one.</p>

                    <h3>3. Company Greetings or Important Announcements</h3>
                    <p>🎂 Want to wish happy birthday to every collaborator or client? Do it with an avatar that says their name and delivers a custom message.<br />
                    📣 Need to announce a new office or an important change? Do it "in person" without having to record a video each time.</p>

                    <h3>4. Multilingual Campaigns Without Additional Costs</h3>
                    <p>🌍 Need to communicate with clients in Germany, Spain, USA? No problem.<br />
                    With a few clicks, your avatar will speak in German, Spanish, or English with a natural and professional voice.</p>

                    <h2>💡 Why Does It Work?</h2>
                    <p>✔ Human but scalable – You have the impact of personal communication without recording thousands of videos manually.<br />
                    ✔ Automatable – Can be integrated with CRM or marketing automation systems.<br />
                    ✔ Simple – You just write a message, the system does everything else.</p>

                    <h2>🚀 What Can It Do for Your Company?</h2>
                    <p>This technology is already available and can be customized to your needs.<br />
                    I can help you:</p>

                    <ul>
                      <li>Implement a cloud solution for personalized video emails</li>
                      <li>Create automated personalized marketing flows</li>
                      <li>Integrate the avatar with your CRM or website</li>
                      <li>Create videos with your company's brand, logo, and visual identity</li>
                    </ul>

                    <h2>🎯 Want to Try a Demo?</h2>
                    <p>Write to me in the comments or privately.<br />
                    📩 I'll send you a customized example for your company (free).<br />
                    💬 Let's talk: one idea is all it takes to transform your company's communication.</p>

                    <p>The future of communication is visual, intelligent, and personal.<br />
                    Those who adopt it first win the attention race.</p>
                  </>
                )}
              </div>
            )}

            {slug === 'why-pmi-fall-behind' && (
              <div className="prose prose-lg dark:prose-invert max-w-none">
                {currentLanguage === 'it' ? (
                  // Italian version
                  <>
                    <p>Non è colpa della crisi. Né dell'AI.</p>
                    <p>È che molte aziende continuano a commettere sempre gli stessi errori.</p>

                    <p>Lavoro ogni giorno con piccole e medie imprese italiane, in settori diversi: pharma, edilizia, retail, produzione.</p>
                    <p>Tutte hanno obiettivi chiari: fare ordine, migliorare i flussi, digitalizzare i processi.</p>

                    <p>Eppure, quando entro in azienda, trovo spesso gli stessi problemi. Gli stessi schemi.</p>
                    <p>Gli stessi errori che rallentano tutto e fanno perdere soldi.</p>

                    <p>Oggi te ne parlo senza filtri.</p>

                    <h2>❌ ERRORE 1 – Pensare che basti "comprare un software"</h2>
                    <p>"Abbiamo preso un gestionale, ora siamo digitali."</p>

                    <p>No.</p>

                    <p>Digitalizzare non vuol dire installare un software.</p>
                    <p>Vuol dire ripensare i processi, semplificarli, integrarli.</p>
                    <p>Un sistema che non riflette i flussi reali dell'azienda crea solo frustrazione.</p>

                    <h3>📍Esempio reale:</h3>
                    <p>Un mio cliente nel settore retail aveva appena acquistato un gestionale da 25.000 euro.</p>
                    <p>Dopo 3 mesi, nessuno lo usava: troppo complicato, poco flessibile, non si adattava alla loro realtà.</p>
                    <p>L'abbiamo sostituito con un'app su misura: ordine, velocità e controllo in 3 settimane.</p>

                    <h2>❌ ERRORE 2 – Affidarsi all'improvvisazione interna</h2>
                    <p>"Ce lo gestisce Mario che è bravo con Excel."</p>
                    <p>"Facciamo tutto con Drive e un paio di automatismi."</p>
                    <p>Oppure: "Abbiamo un ragazzo giovane, smanetta parecchio."</p>

                    <p>Tutto bene, finché non serve scalare, controllare, far lavorare team diversi insieme.</p>
                    <p>Serve una struttura pensata per durare. Non patch temporanee.</p>

                    <p>🧩 Soluzione: strumenti su misura, connessi ai dati reali, costruiti da chi capisce sia il linguaggio umano che quello tecnico.</p>

                    <h2>❌ ERRORE 3 – Nessuno ha il controllo del flusso</h2>
                    <p>Molte PMI italiane sono come un corpo senza sistema nervoso centrale.</p>
                    <p>Ogni reparto va per conto suo: vendite da una parte, magazzino dall'altra, amministrazione isolata.</p>

                    <h3>Il risultato?</h3>
                    <ul>
                      <li>📉 Dati disordinati</li>
                      <li>⏳ Tempi lenti</li>
                      <li>❌ Nessuna visione d'insieme</li>
                    </ul>

                    <p>La vera svolta arriva quando l'azienda decide di centralizzare, automatizzare e dare visibilità ai dati.</p>

                    <h2>🎯 Cosa funziona (davvero)</h2>
                    <ul>
                      <li>✅ Mappatura semplice dei flussi (anche carta e penna)</li>
                      <li>✅ Digitalizzazione su misura, no soluzioni prefabbricate</li>
                      <li>✅ Dashboard visive, app intuitive, automazioni invisibili</li>
                      <li>✅ Supporto diretto, senza call inutili o burocrazia</li>
                    </ul>

                    <p>Con questo approccio, ho aiutato aziende a risparmiare ore al giorno di lavoro manuale e ridurre errori del 90%.</p>

                    <h2>👋 Chi sono (per chi arriva qui per la prima volta)</h2>
                    <p>Mi chiamo Victor, ex chef professionista, oggi consulente indipendente e sviluppatore di soluzioni digitali.</p>
                    <p>Parlo 4 lingue, ma la mia vera forza è una sola:</p>
                    <p>tradurre i bisogni delle persone in strumenti digitali concreti, rapidi e usabili.</p>

                    <p>Lavoro a fianco dei CEO, dei reparti operativi e dei team tecnici.</p>
                    <p>Senza fronzoli. Senza burocrazia. Solo soluzioni che funzionano.</p>

                    <h2>📞 Se sei un imprenditore, un responsabile vendite o un IT manager...</h2>
                    <p>...e riconosci almeno uno di questi errori nella tua azienda, non serve rivoluzionare tutto.</p>
                    <p>Serve partire bene.</p>

                    <h2>🎁 Ti offro una call gratuita di 30 minuti per analizzare insieme dove stai perdendo tempo, dati e opportunità.</h2>

                    <p>👉 Prenota ora la tua consulenza gratuita</p>
                    <p>(Oppure scrivimi direttamente su LinkedIn: <a href="https://www.linkedin.com/in/victor-lazari/" target="_blank" rel="noopener noreferrer">@https://www.linkedin.com/in/victor-lazari/</a>)</p>
                  </>
                ) : (
                  // English version
                  <>
                    <p>It's not the crisis's fault. Nor is it AI's.</p>
                    <p>It's that many companies keep making the same mistakes.</p>

                    <p>I work daily with Italian small and medium enterprises across different sectors: pharma, construction, retail, manufacturing.</p>
                    <p>All have clear objectives: getting organized, improving workflows, digitalizing processes.</p>

                    <p>Yet, when I enter a company, I often find the same problems. The same patterns.</p>
                    <p>The same mistakes that slow everything down and cost money.</p>

                    <p>Today, I'll tell you about them without filters.</p>

                    <h2>❌ MISTAKE 1 – Thinking that "buying software" is enough</h2>
                    <p>"We got a management system, now we're digital."</p>

                    <p>No.</p>

                    <p>Digitalizing doesn't mean installing software.</p>
                    <p>It means rethinking processes, simplifying them, integrating them.</p>
                    <p>A system that doesn't reflect the company's real workflows creates only frustration.</p>

                    <h3>📍Real example:</h3>
                    <p>A retail client had just purchased a €25,000 management system.</p>
                    <p>After 3 months, no one was using it: too complicated, not flexible enough, didn't adapt to their reality.</p>
                    <p>We replaced it with a custom app: order, speed, and control in 3 weeks.</p>

                    <h2>❌ MISTAKE 2 – Relying on internal improvisation</h2>
                    <p>"Mario handles it, he's good with Excel."</p>
                    <p>"We do everything with Drive and a couple of automations."</p>
                    <p>Or: "We have a young guy who's pretty tech-savvy."</p>

                    <p>That's fine until you need to scale, control, make different teams work together.</p>
                    <p>You need a structure designed to last. Not temporary patches.</p>

                    <p>🧩 Solution: custom tools, connected to real data, built by those who understand both human and technical language.</p>

                    <h2>❌ MISTAKE 3 – No one has control of the flow</h2>
                    <p>Many Italian SMEs are like a body without a central nervous system.</p>
                    <p>Each department goes its own way: sales on one side, warehouse on the other, administration isolated.</p>

                    <h3>The result?</h3>
                    <ul>
                      <li>📉 Disorganized data</li>
                      <li>⏳ Slow processes</li>
                      <li>❌ No overall vision</li>
                    </ul>

                    <p>The real breakthrough comes when the company decides to centralize, automate, and make data visible.</p>

                    <h2>🎯 What works (really)</h2>
                    <ul>
                      <li>✅ Simple flow mapping (even with pen and paper)</li>
                      <li>✅ Custom digitalization, no prefabricated solutions</li>
                      <li>✅ Visual dashboards, intuitive apps, invisible automations</li>
                      <li>✅ Direct support, without unnecessary calls or bureaucracy</li>
                    </ul>

                    <p>With this approach, I've helped companies save hours of manual work per day and reduce errors by 90%.</p>

                    <h2>👋 Who I am (for those who are new here)</h2>
                    <p>My name is Victor, former professional chef, now independent consultant and digital solutions developer.</p>
                    <p>I speak 4 languages, but my real strength is one:</p>
                    <p>translating people's needs into concrete, fast, and usable digital tools.</p>

                    <p>I work alongside CEOs, operational departments, and technical teams.</p>
                    <p>Without frills. Without bureaucracy. Only solutions that work.</p>

                    <h2>📞 If you're an entrepreneur, sales manager, or IT manager...</h2>
                    <p>...and you recognize at least one of these mistakes in your company, you don't need to revolutionize everything.</p>
                    <p>You need to start well.</p>

                    <h2>🎁 I offer you a free 30-minute call to analyze together where you're losing time, data, and opportunities.</h2>

                    <p>👉 Book your free consultation now</p>
                    <p>(Or write to me directly on LinkedIn: <a href="https://www.linkedin.com/in/victor-lazari/" target="_blank" rel="noopener noreferrer">@https://www.linkedin.com/in/victor-lazari/</a>)</p>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Render blog list
  return (
    <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto bg-white dark:bg-gray-900 min-h-screen">
      <SEO
        title="Blog – Victor Lazari"
        description="Articles about AI, automation, and digital transformation by Victor Lazari."
        canonical="https://victorlazari.com/blog"
        type="website"
      />
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl md:text-5xl font-semibold text-center mb-12 text-slate-900 dark:text-gray-100"
      >
        {t('blog.title') || 'Blog & Articles'}
      </motion.h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((post, index) => (
          <motion.article
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-slate-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <Link to={`/blog/${post.slug}`} className="absolute inset-0 z-10">
              <span className="sr-only">View article</span>
            </Link>
            <div className="relative h-48 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-40" />
            </div>

            <div className="p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                  className="px-2 py-1 text-xs rounded-full bg-slate-100 dark:bg-gray-700/50 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 flex items-center gap-1"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="text-xl font-semibold mb-3 text-slate-900 dark:text-gray-100 group-hover:text-slate-700 dark:group-hover:text-gray-300 transition-colors">
                {post.title}
              </h2>

              <p className="text-slate-600 dark:text-gray-300 mb-4 line-clamp-2">
                {post.excerpt}
              </p>

              <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  );
};